import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Star, Package, Truck, Shield, Heart, Share2, Loader2, Plus, Minus } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import ProcurementService from '@/api/procurement.service';
import { canCreate } from '@/lib/permissions';

const SupplierMallItemDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [item, setItem] = useState<any>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [relatedItems, setRelatedItems] = useState<any[]>([]);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    if (id) {
      fetchItemDetail();
    }
  }, [id]);

  const fetchItemDetail = async () => {
    try {
      setIsLoading(true);
      const response = await ProcurementService.getSupplierMallItem(businessId, parseInt(id!));
      
      if (response.data?.success) {
        const itemData = response.data.data;
        setItem(itemData);
        if (itemData.variants && itemData.variants.length > 0) {
          setSelectedVariant(itemData.variants[0]);
        }
        fetchRelatedItems(itemData.category);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load item details",
        });
        navigate('/procurement/supplier-mall');
      }
    } catch (error: any) {
      console.error('Error fetching item details:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load item details",
      });
      navigate('/procurement/supplier-mall');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRelatedItems = async (category?: string) => {
    try {
      const response = await ProcurementService.getSupplierMallItems(businessId, { 
        category: category,
        limit: 4,
        exclude: id 
      });
      
      if (response.data?.success) {
        setRelatedItems(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching related items:', error);
    }
  };

  const handleAddToCart = async () => {
    if (!canCreate('purchase_requisitions')) {
      toast({
        variant: "destructive",
        title: "Permission Denied",
        description: "You don't have permission to add items to cart",
      });
      return;
    }

    setIsAddingToCart(true);
    try {
      const cartItem = {
        itemId: item.id,
        supplierId: item.supplier.id,
        quantity: quantity,
        unitPrice: selectedVariant?.price || item.price,
        specifications: selectedVariant || {},
        notes: ''
      };

      const response = await ProcurementService.addToCart(businessId, cartItem);
      
      if (response.data?.success) {
        toast({
          title: "Success",
          description: "Item added to cart successfully",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.data?.message || "Failed to add item to cart",
        });
      }
    } catch (error: any) {
      console.error('Error adding to cart:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add item to cart",
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleCreateRequisition = () => {
    navigate('/procurement/requisitions/create', {
      state: {
        prefilledItems: [{
          itemId: item.id,
          itemName: item.name,
          description: item.description,
          quantity: quantity,
          unitPrice: selectedVariant?.price || item.price,
          supplierId: item.supplier.id,
          supplierName: item.supplier.name
        }]
      }
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getRatingStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  const getAvailabilityBadge = (status: string) => {
    const statusConfig = {
      in_stock: { variant: 'default' as const, label: 'In Stock' },
      low_stock: { variant: 'secondary' as const, label: 'Low Stock' },
      out_of_stock: { variant: 'destructive' as const, label: 'Out of Stock' },
      pre_order: { variant: 'outline' as const, label: 'Pre-Order' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { variant: 'outline' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading item details...</span>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="text-lg font-semibold">Item Not Found</h3>
          <p className="text-muted-foreground">The requested item could not be found.</p>
          <Button 
            onClick={() => navigate('/procurement/supplier-mall')}
            className="mt-4"
          >
            Back to Supplier Mall
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/procurement/supplier-mall')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Supplier Mall
        </Button>
      </div>

      {/* Item Overview */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Images */}
        <div className="space-y-4">
          <div className="aspect-square rounded-lg border bg-muted overflow-hidden">
            {item.images && item.images.length > 0 ? (
              <img
                src={item.images[0]}
                alt={item.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="h-16 w-16 text-muted-foreground" />
              </div>
            )}
          </div>
          
          {item.images && item.images.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {item.images.slice(1, 5).map((image: string, index: number) => (
                <div key={index} className="aspect-square rounded border overflow-hidden">
                  <img
                    src={image}
                    alt={`${item.name} ${index + 2}`}
                    className="w-full h-full object-cover cursor-pointer hover:opacity-80"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Item Details */}
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{item.name}</h1>
            <p className="text-muted-foreground mt-2">{item.description}</p>
          </div>

          {/* Rating and Reviews */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              {getRatingStars(item.rating || 0)}
              <span className="text-sm text-muted-foreground ml-2">
                ({item.reviewCount || 0} reviews)
              </span>
            </div>
            {getAvailabilityBadge(item.availability || 'in_stock')}
          </div>

          {/* Supplier Info */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold">{item.supplier?.name}</h3>
                  <p className="text-sm text-muted-foreground">{item.supplier?.location}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1">
                    {getRatingStars(item.supplier?.rating || 0)}
                  </div>
                  <p className="text-xs text-muted-foreground">Supplier Rating</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Price and Variants */}
          <div className="space-y-4">
            <div>
              <div className="text-3xl font-bold">
                {formatCurrency(selectedVariant?.price || item.price)}
              </div>
              {item.originalPrice && item.originalPrice > (selectedVariant?.price || item.price) && (
                <div className="text-sm text-muted-foreground line-through">
                  {formatCurrency(item.originalPrice)}
                </div>
              )}
              <p className="text-sm text-muted-foreground">per {item.unit || 'unit'}</p>
            </div>

            {/* Variants */}
            {item.variants && item.variants.length > 0 && (
              <div className="space-y-2">
                <Label>Variants</Label>
                <div className="grid grid-cols-2 gap-2">
                  {item.variants.map((variant: any, index: number) => (
                    <Button
                      key={index}
                      variant={selectedVariant?.id === variant.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedVariant(variant)}
                      className="justify-start"
                    >
                      <div className="text-left">
                        <div className="font-medium">{variant.name}</div>
                        <div className="text-xs">{formatCurrency(variant.price)}</div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="space-y-2">
              <Label>Quantity</Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 text-center"
                  min="1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Total: {formatCurrency((selectedVariant?.price || item.price) * quantity)}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <Button
              onClick={handleAddToCart}
              disabled={isAddingToCart || item.availability === 'out_of_stock'}
              className="w-full flex items-center gap-2"
            >
              {isAddingToCart ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ShoppingCart className="h-4 w-4" />
              )}
              Add to Cart
            </Button>
            
            <Button
              variant="outline"
              onClick={handleCreateRequisition}
              className="w-full"
            >
              Create Purchase Requisition
            </Button>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1">
                <Heart className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" size="sm" className="flex-1">
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Information */}
      <Tabs defaultValue="specifications" className="space-y-4">
        <TabsList>
          <TabsTrigger value="specifications">Specifications</TabsTrigger>
          <TabsTrigger value="delivery">Delivery & Returns</TabsTrigger>
          <TabsTrigger value="reviews">Reviews</TabsTrigger>
          <TabsTrigger value="alternatives">Alternatives</TabsTrigger>
        </TabsList>

        <TabsContent value="specifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Product Specifications</CardTitle>
            </CardHeader>
            <CardContent>
              {item.specifications ? (
                <Table>
                  <TableBody>
                    {Object.entries(item.specifications).map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell className="font-medium capitalize">
                          {key.replace(/_/g, ' ')}
                        </TableCell>
                        <TableCell>{String(value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground">No specifications available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="delivery" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Delivery Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center gap-3">
                  <Truck className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Delivery Time</p>
                    <p className="text-sm text-muted-foreground">
                      {item.deliveryTime || '5-7 business days'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Warranty</p>
                    <p className="text-sm text-muted-foreground">
                      {item.warranty || '1 year manufacturer warranty'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Return Policy</h4>
                <p className="text-sm text-muted-foreground">
                  {item.returnPolicy || 'Items can be returned within 30 days of delivery in original condition.'}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reviews" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Customer Reviews</CardTitle>
              <CardDescription>
                {item.reviewCount || 0} reviews with average rating of {item.rating || 0}/5
              </CardDescription>
            </CardHeader>
            <CardContent>
              {item.reviews && item.reviews.length > 0 ? (
                <div className="space-y-4">
                  {item.reviews.map((review: any, index: number) => (
                    <div key={index} className="border-b pb-4 last:border-b-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center gap-1">
                          {getRatingStars(review.rating)}
                        </div>
                        <span className="font-medium">{review.reviewer}</span>
                        <span className="text-sm text-muted-foreground">
                          {new Date(review.date).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm">{review.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No reviews available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alternatives" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Alternative Products</CardTitle>
              <CardDescription>Similar items you might be interested in</CardDescription>
            </CardHeader>
            <CardContent>
              {relatedItems.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {relatedItems.map((relatedItem: any) => (
                    <div
                      key={relatedItem.id}
                      className="border rounded-lg p-4 cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/procurement/supplier-mall/${relatedItem.id}`)}
                    >
                      <div className="flex gap-3">
                        <div className="w-16 h-16 rounded border bg-muted flex items-center justify-center">
                          {relatedItem.images && relatedItem.images[0] ? (
                            <img
                              src={relatedItem.images[0]}
                              alt={relatedItem.name}
                              className="w-full h-full object-cover rounded"
                            />
                          ) : (
                            <Package className="h-6 w-6 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{relatedItem.name}</h4>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {relatedItem.description}
                          </p>
                          <p className="font-semibold mt-1">
                            {formatCurrency(relatedItem.price)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No alternative products available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SupplierMallItemDetail; 